import { defineStore } from 'pinia';
import {
    defaultAemServiceBaseUrl,
    defaultCommwebServiceBaseUrl,
    defaultLedsServiceBaseUrl,
    defaultLegacyCommwebServiceBaseUrl
} from '@/config/service-constants';

import type {
    DeveloperSettingsStoreState
} from '@/types';

type WindowLocationEnvironment = 'production' | 'production-demo' | 'production-mirror' | 'production-staging'
    | 'production-preview' | 'qual' | 'isc' | 'system' | 'unit' | 'localhost';

function updateDeveloperSettingsLocalStorage(settingName: string, settingValue: unknown) {
    const developerSettings: DeveloperSettingsStoreState = JSON.parse(
        localStorage.getItem('mhtkDeveloperSettings') || '{}'
    );
    (developerSettings as unknown as Record<string, unknown>)[settingName] = settingValue;
    try {
        localStorage.setItem('mhtkDeveloperSettings', JSON.stringify(developerSettings));
    } catch (error) {
        console.warn('Error when setting developer setting - ', error);
    }
}

export const useDeveloperSettingsStore = defineStore('developerSettings', {
    state: () => {
        const isAemAuthorEnvironment
            = /^(author1useast1|system-author|qa-author|stg-author|author)\./.test(window.location.hostname);
        const state: DeveloperSettingsStoreState = {
            aemServiceBaseUrl: defaultAemServiceBaseUrl,
            commwebServiceBaseUrl: defaultCommwebServiceBaseUrl,
            ledsServiceBaseUrl: defaultLedsServiceBaseUrl,
            legacyCommwebServiceBaseUrl: defaultLegacyCommwebServiceBaseUrl,
            serviceTestDataScenario: isAemAuthorEnvironment
                || process.env.NODE_ENV === 'development' ? 'default' : null
        };
        return state;
    },
    getters: {
        windowLocationEnvironment(): WindowLocationEnvironment {
            const environmentSwimlaneSplit
                = window.location.hostname.replace('secure.', '').split('.').shift()?.split('-') ?? [];
            return ({
                demo: 'production-demo',
                mirror: 'production-mirror',
                preview: 'production-preview',
                staging: 'production-staging',
                qa: 'qual',
                qa1: 'qual',
                qa2: 'qual',
                mim: 'qual',
                isc1: 'isc',
                isc2: 'isc',
                system: 'system',
                unit: 'unit',
                localhost: 'localhost'
            }[environmentSwimlaneSplit[0]] ?? 'production') as WindowLocationEnvironment;
        },
        windowLocationSwimlane(): number {
            const environmentSwimlaneSplit
                = window.location.hostname.replace('secure.', '').split('.').shift()?.split('-') ?? [];
            return ({
                isc1: 1,
                isc2: 2,
                qa: 1,
                qa1: 1,
                qa2: 2,
                mim: 2
            }[environmentSwimlaneSplit[0]])
                ?? parseInt(environmentSwimlaneSplit[1], 10)
                ?? 1;
        }
    },
    actions: {
        initialize() {
            // Pull settings from local storage
            const developerSettings: DeveloperSettingsStoreState = JSON.parse(
                localStorage.getItem('mhtkDeveloperSettings') || '{}'
            );
            for (const key in this.$state) {
                if (Object.prototype.hasOwnProperty.call(developerSettings, key)) {
                    (this.$state[key as never] as unknown)
                        = developerSettings[key as keyof DeveloperSettingsStoreState];
                }
            }
        },
        setCommwebServiceBaseUrl(commwebServiceBaseUrl: string | null) {
            this.commwebServiceBaseUrl = commwebServiceBaseUrl;
            updateDeveloperSettingsLocalStorage('commwebServiceBaseUrl', commwebServiceBaseUrl);
        },
        setLegacyCommwebServiceBaseUrl(legacyCommwebServiceBaseUrl: string | null) {
            this.legacyCommwebServiceBaseUrl = legacyCommwebServiceBaseUrl;
            updateDeveloperSettingsLocalStorage('legacyCommwebServiceBaseUrl', legacyCommwebServiceBaseUrl);
        },
        setLedsServiceBaseUrl(ledsServiceBaseUrl: string | null) {
            this.ledsServiceBaseUrl = ledsServiceBaseUrl;
            updateDeveloperSettingsLocalStorage('ledsServiceBaseUrl', ledsServiceBaseUrl);
        },
        setServiceTestDataScenario(serviceTestDataScenario: string | null) {
            this.serviceTestDataScenario = serviceTestDataScenario;
            updateDeveloperSettingsLocalStorage('serviceTestDataScenario', serviceTestDataScenario);
        }
    }
});

export type DeveloperSettingsStore = ReturnType<typeof useDeveloperSettingsStore>;
